/**
 * El motor de trabajar sin conexión, en un solo fichero.
 *
 * Las escrituras caen siempre primero en IndexedDB y se encolan; la red es un
 * detalle posterior. Los eventos llevan identificador generado en el cliente,
 * así que reenviar la cola es idempotente, y dos móviles que tocan el mismo
 * evento se resuelven por la última escritura según `updatedAt`.
 */
import type { EventType, HouseholdEvent, PayloadInput } from '@shared/events'
import { deviceTimezone } from '@shared/time'
import { api, OfflineError } from './api'
import { db, readMeta, writeMeta, type HouseholdSnapshot, type StoredEvent } from './db'

const CURSOR_KEY = 'cursor'
const SNAPSHOT_KEY = 'snapshot'
const MAX_ATTEMPTS = 20

export interface SyncResponse {
  serverTime: string
  cursor: number
  events: StoredEvent[]
  rejected: Array<{ id: string; reason: string }>
  snapshot: HouseholdSnapshot
}

export type SyncStatus = 'idle' | 'syncing' | 'offline' | 'error'

interface SyncState {
  status: SyncStatus
  pending: number
  lastSyncAt: string | null
  lastError: string | null
}

type Listener = (state: SyncState) => void

let state: SyncState = { status: 'idle', pending: 0, lastSyncAt: null, lastError: null }
const listeners = new Set<Listener>()
let busy = false
let inFlight: Promise<void> | null = null
let pendingRerun: Promise<void> | null = null

export function subscribeToSync(listener: Listener): () => void {
  listeners.add(listener)
  listener(state)
  return () => listeners.delete(listener)
}

function update(patch: Partial<SyncState>): void {
  state = { ...state, ...patch }
  for (const listener of listeners) listener(state)
}

export function syncState(): SyncState {
  return state
}

/** Encola un evento nuevo o editado. Vuelve en cuanto está a salvo en disco. */
export async function saveEvent(event: HouseholdEvent): Promise<void> {
  const stamped: HouseholdEvent = { ...event, updatedAt: new Date().toISOString() }
  await db.transaction('rw', db.events, db.outbox, async () => {
    await db.events.put(stamped as StoredEvent)
    await db.outbox.add({
      eventId: stamped.id,
      event: stamped,
      queuedAt: new Date().toISOString(),
      attempts: 0,
    })
  })
  update({ pending: await db.outbox.count() })
  void sync()
}

/** Nada se borra de verdad: se marca y se sincroniza como una escritura más. */
export async function deleteEvent(id: string): Promise<void> {
  const existing = await db.events.get(id)
  if (!existing) return
  await saveEvent({ ...existing, deletedAt: new Date().toISOString() })
}

export function newEvent<T extends EventType>(input: {
  type: T
  payload: PayloadInput<T>
  createdBy: string
  occurredAt?: string
  id?: string
}): HouseholdEvent {
  const now = new Date().toISOString()
  return {
    id: input.id ?? crypto.randomUUID(),
    type: input.type,
    occurredAt: input.occurredAt ?? now,
    tz: deviceTimezone(),
    payload: input.payload as Record<string, unknown>,
    createdBy: input.createdBy,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
  }
}

/** Reduce la cola a una mutación por evento: gana la más nueva. */
async function pendingMutations(): Promise<{ mutations: HouseholdEvent[]; keys: number[] }> {
  const entries = await db.outbox.orderBy('key').toArray()
  const latest = new Map<string, HouseholdEvent>()
  const keys: number[] = []
  for (const entry of entries) {
    if (entry.attempts >= MAX_ATTEMPTS) continue
    latest.set(entry.eventId, entry.event)
    if (entry.key !== undefined) keys.push(entry.key)
  }
  return { mutations: [...latest.values()], keys }
}

/**
 * Quien llama recibe siempre una promesa que se resuelve cuando *sus* datos
 * han tenido su turno: lo que se encole mientras hay una vuelta en marcha
 * comparte una única vuelta siguiente, en vez de devolver el control creyendo
 * que la cola ya se vació.
 */
export function sync(): Promise<void> {
  if (!busy) return startRun()
  if (!pendingRerun) {
    pendingRerun = (inFlight ?? Promise.resolve()).then(() => {
      pendingRerun = null
      return startRun()
    })
  }
  return pendingRerun
}

function startRun(): Promise<void> {
  busy = true
  inFlight = runOnce().finally(() => {
    busy = false
    inFlight = null
  })
  return inFlight
}

async function runOnce(): Promise<void> {
  update({ status: 'syncing' })

  try {
    const cursor = await readMeta<number>(CURSOR_KEY, 0)
    const { mutations, keys } = await pendingMutations()
    const response = await api.post<SyncResponse>('/api/sync', { cursor, mutations })

    await db.transaction('rw', db.events, db.outbox, db.meta, async () => {
      // Sólo se quitan de la cola las entradas que se enviaron de verdad; lo
      // que entró mientras la petición volaba sobrevive a la vuelta siguiente.
      if (keys.length > 0) await db.outbox.bulkDelete(keys)
      if (response.events.length > 0) await db.events.bulkPut(response.events)
      await db.meta.put({ key: CURSOR_KEY, value: response.cursor })
      await db.meta.put({ key: SNAPSHOT_KEY, value: response.snapshot })
    })

    if (response.rejected.length > 0) {
      console.warn('[sync] el servidor rechazó eventos', response.rejected)
    }

    update({
      status: 'idle',
      pending: await db.outbox.count(),
      lastSyncAt: response.serverTime,
      lastError: response.rejected[0]?.reason ?? null,
    })
  } catch (error) {
    if (error instanceof OfflineError) {
      update({ status: 'offline', pending: await db.outbox.count() })
    } else {
      await noteFailure((error as Error).message)
      update({
        status: 'error',
        lastError: (error as Error).message,
        pending: await db.outbox.count(),
      })
    }
  }
}

async function noteFailure(message: string): Promise<void> {
  const entries = await db.outbox.toArray()
  await db.transaction('rw', db.outbox, async () => {
    for (const entry of entries) {
      if (entry.key === undefined) continue
      await db.outbox.update(entry.key, { attempts: entry.attempts + 1, lastError: message })
    }
  })
}

export async function loadSnapshot(): Promise<HouseholdSnapshot | null> {
  return readMeta<HouseholdSnapshot | null>(SNAPSHOT_KEY, null)
}

export async function resetCursor(): Promise<void> {
  await writeMeta(CURSOR_KEY, 0)
}

let interval: number | null = null

/** Sincroniza en los cuatro momentos que importan: arranque, foco, reconexión y un tic lento. */
export function startSyncLoop(): () => void {
  const onOnline = (): void => void sync()
  const onVisible = (): void => {
    if (document.visibilityState === 'visible') void sync()
  }
  window.addEventListener('online', onOnline)
  document.addEventListener('visibilitychange', onVisible)
  interval = window.setInterval(() => void sync(), 60_000)
  void sync()

  return () => {
    window.removeEventListener('online', onOnline)
    document.removeEventListener('visibilitychange', onVisible)
    if (interval) window.clearInterval(interval)
    interval = null
  }
}
